"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "@/context/CartContext";
import { useRouter } from "next/navigation";

const navLinks = [
  { label: "Home",         href: "/homepage",   type: "link"   },
  { label: "Our Story",    href: "/our-story",  type: "link"   },
  { label: "Benefits",     href: "#benefits",   type: "anchor" },
  { label: "Ingredients",  href: "#ingredients", type: "anchor" },
  { label: "Blog",         href: "/blog",       type: "link"   },
  { label: "Shop",         href: "/purchase",   type: "link"   },
];

export default function Header() {
  const [scrolled,     setScrolled]     = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const { totalItems } = useCart();
  const router = useRouter();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      router.push(`/homepage${href}`);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-sm border-b border-pmu-taupe-light/20" :"bg-pmu-cream/95 border-b border-pmu-taupe-light/15"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-18 flex items-center justify-between" style={{ height: "72px" }}>
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2.5">
          <Image
            src="/assets/images/Pick_Me_Up-branding_logomark-blue-09-1773749520847.png"
            alt="Pick Me Up logo"
            width={110}
            height={44}
            className="object-contain h-10 w-auto"
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.type === "link" ? (
              <Link
                key={link.label}
                href={link.href}
                className="nav-link text-pmu-amber"
              >
                {link.label}
              </Link>
            ) : link.type === "anchor" ? (
              <button
                key={link.label}
                onClick={() => handleAnchor(link.href)}
                className="nav-link text-pmu-amber"
              >
                {link.label}
              </button>
            ) : null
          )}
        </div>

        {/* CTA + Cart */}
        <div className="hidden md:flex items-center gap-3">
          {/* Cart Icon */}
          <Link
            href="/checkout"
            className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-pmu-amber-pale transition-colors"
            aria-label="View cart"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-pmu-espresso">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-pmu-amber text-pmu-espresso text-xs font-800 w-5 h-5 rounded-full flex items-center justify-center leading-none" style={{ fontSize: "10px", fontWeight: 700 }}>
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </Link>

          <Link href="/purchase" className="btn-primary text-xs">
            Order Now
          </Link>
        </div>

        {/* Mobile: Cart + Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <Link
            href="/checkout"
            className="relative flex items-center justify-center w-9 h-9 rounded-md hover:bg-pmu-amber-pale transition-colors"
            aria-label="View cart"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-pmu-espresso">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-pmu-amber text-pmu-espresso text-xs font-700 w-4 h-4 rounded-full flex items-center justify-center leading-none" style={{ fontSize: "9px", fontWeight: 700 }}>
                {totalItems > 9 ? "9+" : totalItems}
              </span>
            )}
          </Link>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-1.5 p-2 rounded-md hover:bg-pmu-amber-pale transition-colors"
            aria-label="Toggle menu"
          >
            <span className={`block w-5 h-0.5 bg-pmu-espresso rounded transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block w-5 h-0.5 bg-pmu-espresso rounded transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block w-5 h-0.5 bg-pmu-espresso rounded transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } bg-white border-b border-pmu-taupe-light/20`}
      >
        <div className="px-6 py-5 flex flex-col gap-1">
          {navLinks.map((link) =>
            link.type === "link" ? (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-left text-xs font-700 text-pmu-amber uppercase tracking-widest py-3 border-b border-pmu-taupe-light/15 last:border-0"
                style={{ fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {link.label}
              </Link>
            ) : link.type === "anchor" ? (
              <button
                key={link.label}
                onClick={() => handleAnchor(link.href)}
                className="text-left text-xs font-700 text-pmu-amber uppercase tracking-widest py-3 border-b border-pmu-taupe-light/15 last:border-0"
                style={{ fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {link.label}
              </button>
            ) : null
          )}
          <Link href="/purchase" onClick={() => setMenuOpen(false)} className="btn-primary mt-3 justify-center text-xs">
            Order Now
          </Link>
        </div>
      </div>
    </header>
  );
}